/**
 * QuoteRequestServices.js
 *
 * Servicios para el sistema de solicitudes de cotización a proveedores similares.
 * Incluye funciones para compradores y proveedores.
 */

const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL;

// =====================================================
// CART FUNCTIONS (Desde el carrito)
// =====================================================

/**
 * Detecta proveedores disponibles para los productos del carrito.
 *
 * @param {string} token - Token de autenticación
 * @param {Array} products - Lista de productos del carrito [{id, name, measures}]
 * @returns {Promise<Object>} - Proveedores detectados
 */
export const detectCartProviders = async (token, products) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/cart/detect-providers`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ products })
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al detectar proveedores.');
    }

    return await response.json();
};

/**
 * Envía solicitudes de cotización desde el carrito a proveedores seleccionados.
 *
 * @param {string} token - Token de autenticación
 * @param {Array} products - Lista de productos del carrito
 * @param {Array<string>} providerIds - IDs de proveedores seleccionados
 * @param {string} specifications - Especificaciones generales
 * @param {string} notes - Notas adicionales
 * @returns {Promise<Object>} - Resultado del envío
 */
export const sendCartQuoteRequests = async (
    token,
    products,
    providerIds,
    specifications = '',
    notes = ''
) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/quote-requests/cart/send`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            products,
            provider_ids: providerIds,
            specifications,
            notes
        })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al enviar solicitudes.');
    }

    return await response.json();
};

// =====================================================
// BUYER FUNCTIONS (Compradores)
// =====================================================

/**
 * Detecta proveedores similares para un producto (paso de preview).
 *
 * @param {string} token - Token de autenticación
 * @param {string} productId - ID del producto de referencia
 * @param {number} limit - Número máximo de proveedores a retornar
 * @returns {Promise<Object>} - Datos de proveedores similares
 */
export const detectSimilarProviders = async (token, productId, limit = 10) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/detect-providers/${productId}?limit=${limit}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al detectar proveedores similares.');
    }

    return await response.json();
};

/**
 * Crea una solicitud de cotización y notifica a los proveedores.
 *
 * @param {string} token - Token de autenticación
 * @param {string} productId - ID del producto de referencia
 * @param {string} specifications - Especificaciones del comprador
 * @param {number} quantity - Cantidad requerida
 * @param {string|null} deliveryDeadline - Fecha límite de entrega (ISO string)
 * @param {string} notes - Notas adicionales
 * @returns {Promise<Object>} - Datos de la solicitud creada
 */
export const createQuoteRequest = async (
    token,
    productId,
    specifications = '',
    quantity = 1,
    deliveryDeadline = null,
    notes = ''
) => {
    const response = await fetch(`${REACT_APP_BFF_URL}/quote-requests`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            product_id: productId,
            specifications,
            quantity,
            delivery_deadline: deliveryDeadline,
            notes
        })
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al crear solicitud de cotización.');
    }

    return await response.json();
};

/**
 * Obtiene todas las solicitudes de cotización del comprador actual.
 *
 * @param {string} token - Token de autenticación
 * @param {number} limit - Número máximo de resultados
 * @returns {Promise<Object>} - Lista de solicitudes
 */
export const getMyQuoteRequests = async (token, limit = 50) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/my-requests?limit=${limit}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al obtener solicitudes de cotización.');
    }

    return await response.json();
};

/**
 * Obtiene los detalles de una solicitud de cotización específica.
 *
 * @param {string} token - Token de autenticación
 * @param {string} quoteRequestId - ID de la solicitud
 * @returns {Promise<Object>} - Detalles de la solicitud
 */
export const getQuoteRequest = async (token, quoteRequestId) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/${quoteRequestId}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al obtener detalles de la solicitud.');
    }

    const result = await response.json();
    // La API devuelve {success: true, data: quote_request}
    return result.data || result;
};

/**
 * Acepta la cotización de un proveedor.
 *
 * @param {string} token - Token de autenticación
 * @param {string} quoteRequestId - ID de la solicitud
 * @param {string} providerId - ID del proveedor cuya cotización se acepta
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const acceptQuote = async (token, quoteRequestId, providerId) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/${quoteRequestId}/accept/${providerId}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al aceptar cotización.');
    }

    return await response.json();
};

/**
 * Cancela una solicitud de cotización.
 *
 * @param {string} token - Token de autenticación
 * @param {string} quoteRequestId - ID de la solicitud
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const cancelQuoteRequest = async (token, quoteRequestId) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/${quoteRequestId}`,
        {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al cancelar solicitud.');
    }

    return await response.json();
};

/**
 * Reenvía los emails de notificación a los proveedores.
 *
 * @param {string} token - Token de autenticación
 * @param {string} quoteRequestId - ID de la solicitud
 * @returns {Promise<Object>} - Resultado del reenvío
 */
export const resendQuoteRequestEmails = async (token, quoteRequestId) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/${quoteRequestId}/resend-emails`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al reenviar emails.');
    }

    return await response.json();
};

/**
 * Crea pedidos por proveedor a partir de una solicitud de cotizacion.
 *
 * @param {string} token - Token de autenticacion
 * @param {string} quoteRequestId - ID de la solicitud
 * @param {Array} items - Items seleccionados [{provider_id, product_id, requested_product_id?}]
 * @returns {Promise<Object>} - Resultado de la operacion
 */
export const createPurchaseOrders = async (token, quoteRequestId, items = []) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/purchase-orders/from-quote/${quoteRequestId}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ items })
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al crear pedidos.');
    }

    return await response.json();
};

// =====================================================
// PROVIDER FUNCTIONS (Proveedores)
// =====================================================

/**
 * Obtiene las solicitudes de cotización entrantes para el proveedor actual.
 *
 * @param {string} token - Token de autenticación
 * @param {number} limit - Número máximo de resultados
 * @returns {Promise<Object>} - Lista de solicitudes entrantes
 */
export const getIncomingQuoteRequests = async (token, limit = 50) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/provider/incoming?limit=${limit}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (response.status === 204) {
        return { success: true, data: [], count: 0 };
    }

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        if (response.status === 404) {
            return { success: true, data: [], count: 0 };
        }
        throw new Error(error.detail || 'Error al obtener solicitudes entrantes.');
    }

    const payload = await response.json().catch(() => null);
    if (!payload) {
        return { success: true, data: [], count: 0 };
    }

    if (Array.isArray(payload)) {
        return { success: true, data: payload, count: payload.length };
    }

    if (Array.isArray(payload.data)) {
        return payload;
    }

    return { success: true, data: [], count: 0 };
};

/**
 * Envía una respuesta/cotización a una solicitud.
 *
 * @param {string} token - Token de autenticación
 * @param {string} quoteRequestId - ID de la solicitud
 * @param {number} price - Precio cotizado
 * @param {string} currency - Moneda (USD, ARS, etc.)
 * @param {number} deliveryDays - Días de entrega estimados
 * @param {string} notes - Notas del proveedor
 * @param {Array<string>} attachments - URLs de archivos adjuntos
 * @param {Array<Object>} productQuotes - Precios por producto (solo carrito)
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const respondToQuoteRequest = async (
    token,
    quoteRequestId,
    price,
    currency = 'ARS',
    deliveryDays,
    notes = '',
    attachments = [],
    productQuotes = [],
    unitPrice = null
) => {
    const payload = {
        price,
        currency,
        delivery_days: deliveryDays,
        notes,
        attachments
    };

    // Agregar precio unitario si se proporciona (para solicitudes simples)
    if (unitPrice !== null) {
        payload.unit_price = unitPrice;
    }

    if (Array.isArray(productQuotes) && productQuotes.length > 0) {
        payload.product_quotes = productQuotes;
    }

    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/${quoteRequestId}/respond`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al enviar cotización.');
    }

    return await response.json();
};

/**
 * Rechaza responder a una solicitud de cotización.
 *
 * @param {string} token - Token de autenticación
 * @param {string} quoteRequestId - ID de la solicitud
 * @returns {Promise<Object>} - Resultado de la operación
 */
export const declineQuoteRequest = async (token, quoteRequestId) => {
    const response = await fetch(
        `${REACT_APP_BFF_URL}/quote-requests/${quoteRequestId}/decline`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            }
        }
    );

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.detail || 'Error al rechazar solicitud.');
    }

    return await response.json();
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Obtiene el estado legible de una solicitud.
 *
 * @param {string} status - Estado de la solicitud
 * @returns {Object} - Objeto con label y color
 */
export const getQuoteRequestStatusInfo = (status) => {
    const statusMap = {
        'pending': { label: 'Pendiente', color: 'default' },
        'sent': { label: 'Enviada', color: 'processing' },
        'partially_responded': { label: 'Respuestas parciales', color: 'warning' },
        'all_responded': { label: 'Todas respondidas', color: 'success' },
        'completed': { label: 'Completada', color: 'success' },
        'cancelled': { label: 'Cancelada', color: 'error' }
    };

    return statusMap[status] || { label: status, color: 'default' };
};

/**
 * Obtiene el estado legible de una respuesta de proveedor.
 *
 * @param {string} status - Estado de la respuesta
 * @returns {Object} - Objeto con label y color
 */
export const getProviderResponseStatusInfo = (status) => {
    const statusMap = {
        'pending': { label: 'Pendiente', color: 'default' },
        'draft': { label: 'Borrador', color: 'warning' },
        'responded': { label: 'Cotizado', color: 'success' },
        'declined': { label: 'Rechazado', color: 'error' }
    };

    return statusMap[status] || { label: status, color: 'default' };
};

/**
 * Formatea el precio con moneda.
 *
 * @param {number} price - Precio
 * @param {string} currency - Moneda
 * @returns {string} - Precio formateado
 */
export const formatPrice = (price, currency = 'ARS') => {
    if (price === null || price === undefined) return '-';

    return new Intl.NumberFormat('es-AR', {
        style: 'currency',
        currency: currency
    }).format(price);
};
